import React from 'react';
import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { statusLabel } from '../../i18n/translate';
import { InvoiceRecord, normalizeInvoiceStatus } from '../../types/invoice';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getInvoiceBillToInfo } from '../../utils/billing';

type InvoiceDetailsPanelProps = {
  invoice: InvoiceRecord;
  actions?: React.ReactNode;
  metaControl?: React.ReactNode;
  showPaymentTotals?: boolean;
};

type InvoiceStatusColor = 'success' | 'warning' | 'error' | 'info' | 'default';

const getInvoiceStatusColor = (status: string): InvoiceStatusColor => {
  switch (normalizeInvoiceStatus(status)) {
    case 'PAID':
      return 'success';
    case 'UNPAID':
      return 'warning';
    case 'OVERDUE':
      return 'error';
    case 'SENT':
      return 'info';
    case 'CANCELLED':
      return 'default';
    default:
      return 'default';
  }
};

type DetailCardProps = {
  label: string;
  children: React.ReactNode;
  emphasize?: boolean;
};

const DetailCard: React.FC<DetailCardProps> = ({ label, children, emphasize = false }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.75,
      height: '100%',
      borderRadius: 2,
      borderColor: emphasize ? 'primary.light' : 'divider',
      bgcolor: emphasize ? 'action.hover' : 'background.paper',
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: 'block',
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    {children}
  </Paper>
);

type InfoFieldProps = {
  label: string;
  value: React.ReactNode;
};

const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

export const InvoiceDetailsPanel: React.FC<InvoiceDetailsPanelProps> = ({
  invoice,
  actions,
  metaControl,
  showPaymentTotals = false,
}) => {
  const { t } = useTranslation();
  const billTo = getInvoiceBillToInfo(invoice);
  const unavailableText = t('common.unavailable', { defaultValue: 'N/A' });

  return (
    <Paper elevation={1} sx={{ p: { xs: 1.75, md: 2 } }}>
      {actions ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1.25,
          }}
        >
          {actions}
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 1.25,
          mb: 1.75,
        }}
      >
        <DetailCard label={t('fields.invoice', { defaultValue: 'Invoice' })}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
              flexWrap: 'wrap',
              mb: 0.75,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {invoice.invoiceNumber}
              </Typography>
              <Chip
                label={statusLabel(t, normalizeInvoiceStatus(invoice.status) ?? invoice.status)}
                color={getInvoiceStatusColor(invoice.status)}
                size="small"
              />
            </Box>
            {metaControl ? <Box sx={{ display: 'flex', alignItems: 'center' }}>{metaControl}</Box> : null}
          </Box>
        </DetailCard>
        <DetailCard label={t('invoices.subtotalExclVat')}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {formatCurrency(invoice.subtotal)}
          </Typography>
        </DetailCard>
        <DetailCard label={t('invoices.vatPercent')}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {formatCurrency(invoice.taxAmount || 0)}
          </Typography>
        </DetailCard>
        <DetailCard label={t('fields.totalAmount')} emphasize>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {formatCurrency(invoice.totalAmount)}
          </Typography>
        </DetailCard>
        {showPaymentTotals ? (
          <DetailCard label={t('fields.amountPaid', { defaultValue: 'Amount Paid' })}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formatCurrency(invoice.amountPaid || 0)}
            </Typography>
          </DetailCard>
        ) : null}
        {showPaymentTotals ? (
          <DetailCard label={t('fields.balance', { defaultValue: 'Balance' })}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formatCurrency(invoice.balanceDue ?? invoice.totalAmount)}
            </Typography>
          </DetailCard>
        ) : null}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <DetailCard label={t('common.overview', { defaultValue: 'Overview' })}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 1.5,
              }}
            >
              <InfoField label={t('fields.invoiceDate')} value={formatDate(invoice.invoiceDate)} />
              <InfoField
                label={t('fields.dueDate')}
                value={invoice.dueDate ? formatDate(invoice.dueDate) : '-'}
              />
              <InfoField
                label={t('fields.paymentMethod')}
                value={invoice.paymentMethod || unavailableText}
              />
              <InfoField
                label={t('fields.paidDate', { defaultValue: 'Paid Date' })}
                value={invoice.paidDate ? formatDate(invoice.paidDate) : '-'}
              />
            </Box>
          </DetailCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <DetailCard label={t('fields.billTo', { defaultValue: 'Bill To' })}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
              {billTo.displayName}
            </Typography>
            {billTo.contactPersonName ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {billTo.contactPersonName}
              </Typography>
            ) : null}
            <Box sx={{ display: 'grid', gap: 0.75 }}>
              <InfoField label={t('fields.email')} value={billTo.email || unavailableText} />
              <InfoField
                label={t('fields.phoneNumber', { defaultValue: 'Phone Number' })}
                value={billTo.phoneNumber || unavailableText}
              />
            </Box>
          </DetailCard>
        </Grid>

        {invoice.description ? (
          <Grid item xs={12}>
            <DetailCard label={t('fields.description')}>
              <Typography variant="body1">{invoice.description}</Typography>
            </DetailCard>
          </Grid>
        ) : null}
      </Grid>
    </Paper>
  );
};
